<template>
    <div class="-mt-8">
        <ValidationObserver ref="personalDetailsObserver">
            <ValidationObserver v-slot="validation">
                <AccordionComponent
                    label="Personal Details"
                    :status="doGetValidationFormStatus(personalDetails, validation)"
                    show
                >
                    <div class="mb-6">
                        <DropdownComponent
                            v-model="personalDetails.title"
                            :options="collections.titles"
                            name="title"
                            label="Title"
                            placeholder="- Please Select -"
                            @change="updatePersonalDetails"
                            :searchable="false"
                        />
                    </div>

                    <div class="mb-6">
                        <InputComponent
                            v-model="personalDetails.first_name"
                            label="First Name (s)"
                            placeholder="First Name (s)"
                            name="first_name"
                            @change="updatePersonalDetails"
                        />
                    </div>

                    <div class="mb-6">
                        <InputComponent
                            v-model="personalDetails.middle_name"
                            label="Middle Name (s)"
                            placeholder="Middle Name (s)"
                            name="middle_name"
                            rules="optional"
                            @change="updatePersonalDetails"
                        />
                    </div>

                    <div class="mb-6">
                        <InputComponent
                            v-model="personalDetails.last_name"
                            label="Surname"
                            placeholder="Surname"
                            name="last_name"
                            @change="updatePersonalDetails"
                        />
                    </div>

                    <div class="mb-6">
                        <InputComponent
                            v-model="personalDetails.previous_name"
                            label="Previous Name"
                            placeholder="Previous Name"
                            name="previous_name"
                            rules="optional"
                            @change="updatePersonalDetails"
                        />
                    </div>

                    <div class="mb-6">
                        <DropdownComponent
                            v-model="personalDetails.nationality"
                            :options="collections.countries"
                            :reduce="(option) => option.iso_code_2"
                            name="nationality"
                            label="Nationality"
                            placeholder="- Please Select -"
                            hint="The status of belonging to a particular nation by origin, birth, or naturalisation. Nationality is a legal identification of a person in international law, establishing the person as a subject, a national, of a sovereign state."
                            @change="updatePersonalDetails"
                            isCountryList
                            checkIsSelectable="enable_in_nationality"
                        />
                    </div>

                    <div class="mb-6">
                        <AddNewButtonComponent
                            label="Add Dual Nationality"
                            @click="doShowOtherNationalityField"
                            :disabled="showOtherNationalityField || !!personalDetailsState.details.other_nationality"
                        />
                    </div>

                    <transition
                        enter-active-class="transition ease-out duration-500 transform"
                        enter-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition ease-out duration-200 transform"
                        leave-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <div
                            v-if="showOtherNationalityField || !!personalDetailsState.details.other_nationality"
                            class="mb-6"
                        >
                            <DropdownComponent
                                v-model="personalDetails.other_nationality"
                                :options="collections.countries"
                                :reduce="(option) => option.iso_code_2"
                                name="other_nationality"
                                label="Other Nationality"
                                placeholder="- Please Select -"
                                @change="updatePersonalDetails"
                                rules="optional"
                                isCountryList
                                checkIsSelectable="enable_in_dual_nationality"
                            />
                        </div>
                    </transition>

                    <div class="mb-6">
                        <DropdownComponent
                            v-model="personalDetails.citizenship"
                            :options="collections.countries"
                            :reduce="(option) => option.iso_code_2"
                            name="citizenship"
                            label="Citizenship"
                            placeholder="- Please Select -"
                            hint="The definition of citizenship is the status of being a citizen, along with the rights, duties and privileges of being a citizen. An example of citizenship is someone being born in the United States and having access to all the same freedoms and rights as those already living in the US."
                            @change="updatePersonalDetails"
                            isCountryList
                            checkIsSelectable="enable_in_citizenship"
                        />
                    </div>

                    <div class="mb-6">
                        <DatepickerComponent
                            v-model="personalDetails.date_of_birth"
                            name="date_of_birth"
                            label="Date Of Birth"
                            subtext="You need to be 18 or over to create an account."
                            @change="updatePersonalDetails"
                        />
                    </div>

                    <div class="mb-6 w-1/2">
                        <InputComponent
                            v-model="personalDetails.number_of_dependents"
                            type="number"
                            rules="required|integer|min_value:0"
                            label="Number of Dependents"
                            placeholder="Number of Dependents"
                            name="number_of_dependents"
                            hint="A dependant is a person who relies on another as a primary source of income. e.g A child under the age of 18 years."
                            @change="updatePersonalDetails"
                            min="0"
                        />
                    </div>

                    <div class="mb-6">
                        <DropdownComponent
                            v-model="personalDetails.tax_residence_jurisdiction"
                            :options="collections.countries"
                            :reduce="(option) => option.iso_code_2"
                            name="tax_residence_jurisdiction"
                            label="Tax Residence Jurisdiction"
                            hint="Generally, you’ll be tax resident in the country where you live, but other factors may be relevant. We aren’t able to provide tax advice so if you’re unsure about your tax residency status, please speak with a professional tax adviser"
                            placeholder="- Please Select -"
                            @change="updatePersonalDetails"
                            isCountryList
                            checkIsSelectable="enable_in_residency"
                        />
                    </div>

                    <div>
                        <InputComponent
                            classNames="uppercase"
                            v-model="personalDetails.tin"
                            label="TIN - Tax Identification Number"
                            placeholder="TIN - Tax Identification Number"
                            name="tin"
                            hint='TIN - Tax Identification Number (i.e. National Insurance number for UK) - for other global standard <a href="https://www.oecd.org/tax/automatic-exchange/crs-implementation-and-assistance/tax-identification-numbers/" target="_blank" rel="noopener noreferrer">click here</a>.'
                            @change="updatePersonalDetails"
                        />
                    </div>
                </AccordionComponent>
            </ValidationObserver>

            <ValidationObserver v-slot="validation">
                <AccordionComponent
                    show
                    label="Address"
                    :status="
                        doGetValidationFormStatus(
                            { current: { ...addressDetails.current }, mailing: { ...addressDetails.mailing } },
                            validation
                        )
                    "
                >
                    <h2 class="mb-6">
                        This address needs to be the same as the utility bill you provide as proof to open the account.
                    </h2>
                    <div class="mb-6">
                        <InputComponent
                            label="Street Address"
                            placeholder="Street Address"
                            name="street_address"
                            v-model="addressDetails.current.street_address"
                            @change="updateAddressDetails"
                        />
                    </div>

                    <div class="mb-6">
                        <InputComponent
                            label="Town/City"
                            placeholder="Town/City"
                            name="city"
                            v-model="addressDetails.current.city"
                            @change="updateAddressDetails"
                        />
                    </div>

                    <div class="mb-6 w-1/2">
                        <InputComponent
                            label="Post Code"
                            placeholder="Post Code"
                            name="postcode"
                            v-model="addressDetails.current.postcode"
                            @change="updateAddressDetails"
                        />
                    </div>

                    <div class="mb-6">
                        <DropdownComponent
                            :options="collections.countries"
                            :reduce="(option) => option.iso_code_2"
                            name="country"
                            label="Country"
                            placeholder="- Please Select -"
                            v-model="addressDetails.current.country"
                            @change="updateAddressDetails"
                            isCountryList
                            checkIsSelectable="enable_in_residency"
                        />
                    </div>

                    <div class="mb-6 w-1/2">
                        <InputComponent
                            type="number"
                            label="Duration at Address (years)"
                            placeholder="Duration"
                            name="duration_at_address"
                            rules="required|integer"
                            v-model="addressDetails.current.duration_at_address"
                            @change="updateAddressDetails"
                        />
                    </div>

                    <!-- Show only if the duration_at_address is < 3 -->
                    <transition
                        enter-active-class="transition ease-out duration-500 transform"
                        enter-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition ease-out duration-200 transform"
                        leave-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <div
                            v-if="
                                addressDetails.current != undefined &&
                                addressDetails.current.duration_at_address &&
                                addressDetails.current.duration_at_address < 3
                            "
                        >
                            <h2 class="mb-6">Previous Address</h2>

                            <div class="mb-6">
                                <InputComponent
                                    label="Previous Street Address"
                                    placeholder="Previous Street Address"
                                    name="previous_street_address"
                                    v-model="addressDetails.previous.street_address"
                                    @change="updateAddressDetails"
                                />
                            </div>

                            <div class="mb-6">
                                <InputComponent
                                    label="Previous Town/City"
                                    placeholder="Previous Town/City"
                                    name="previous_city"
                                    v-model="addressDetails.previous.city"
                                    @change="updateAddressDetails"
                                />
                            </div>

                            <div class="mb-6 w-1/2">
                                <InputComponent
                                    label="Previous Post Code"
                                    placeholder="Previous Post Code"
                                    name="previous_postcode"
                                    v-model="addressDetails.previous.postcode"
                                    @change="updateAddressDetails"
                                />
                            </div>

                            <div class="mb-6">
                                <DropdownComponent
                                    :options="collections.countries"
                                    :reduce="(option) => option.iso_code_2"
                                    name="previous_country"
                                    label="Previous Country"
                                    placeholder="Previous Country"
                                    v-model="addressDetails.previous.country"
                                    @change="updateAddressDetails"
                                    isCountryList
                                />
                            </div>
                        </div>
                    </transition>
                    <!-- END Show only if the duration_at_address is < 3 -->

                    <div class="mb-6">
                        <h2>Mailing Address</h2>
                    </div>

                    <div class="mb-6">
                        <YesNoSliderComponent
                            name="is_mailing_address_different"
                            label="Mailing Address different to Permanent Address?"
                            v-model="isMailingAddressDifferent"
                            @change="setIsMailingAddressDifferent"
                        />
                    </div>

                    <!-- Only show if it is selected that the mailing address is different to the permanent address -->
                    <transition
                        enter-active-class="transition ease-out duration-500 transform"
                        enter-class="opacity-0"
                        enter-to-class="opacity-100"
                        leave-active-class="transition ease-out duration-200 transform"
                        leave-class="opacity-100"
                        leave-to-class="opacity-0"
                    >
                        <div v-if="isMailingAddressDifferent">
                            <div class="mb-6">
                                <InputComponent
                                    name="mailing_street_address"
                                    label="Mailing Street Address"
                                    placeholder="Mailing Street Address"
                                    v-model="addressDetails.mailing.street_address"
                                    @change="updateAddressDetails"
                                />
                            </div>

                            <div class="mb-6">
                                <InputComponent
                                    name="mailing_city"
                                    label="Mailing Town/City"
                                    placeholder="Mailing Town/City"
                                    v-model="addressDetails.mailing.city"
                                    @change="updateAddressDetails"
                                />
                            </div>

                            <div class="mb-6">
                                <InputComponent
                                    label="Mailing Post Code"
                                    placeholder="Mailing Post Code"
                                    name="mailing_postcode"
                                    v-model="addressDetails.mailing.postcode"
                                    @change="updateAddressDetails"
                                />
                            </div>

                            <div class="mb-6">
                                <DropdownComponent
                                    :options="collections.countries"
                                    :reduce="(option) => option.iso_code_2"
                                    name="mailing_country"
                                    label="Mailing Country"
                                    placeholder="- Please Select -"
                                    v-model="addressDetails.mailing.country"
                                    @change="updateAddressDetails"
                                    isCountryList
                                    checkIsSelectable="enable_in_residency"
                                />
                            </div>
                        </div>
                    </transition>
                    <!--END Only show if it is selected that the mailing address is different to the permanent address -->
                </AccordionComponent>
            </ValidationObserver>

            <ValidationObserver v-slot="validation">
                <AccordionComponent
                    show
                    label="Contact Details"
                    :status="doGetValidationFormStatus(contactDetails, validation)"
                    doAnchor="contact"
                >
                    <div class="mb-6">
                        <InputComponent
                            disabled
                            type="email"
                            label="Email Address"
                            placeholder="Email Address"
                            name="email"
                            v-model="user.email"
                        />
                    </div>
                    <div class="mb-6">
                        <TelephoneComponent
                            label="Telephone (Landline)"
                            placeholder="Telephone (Landline)"
                            name="telephone_no"
                            :telephone_no="contactDetails.telephone_no"
                            @telephone_no="contactDetails.telephone_no = $event"
                            :telephone_code="contactDetails.telephone_international_dialling_code"
                            @telephone_code="contactDetails.telephone_international_dialling_code = $event"
                            @change="updateContactDetails"
                            :options="collections.countries"
                        />
                    </div>
                    <div class="mb-6">
                        <TelephoneComponent
                            label="Telephone (Mobile)"
                            placeholder="Telephone (Mobile)"
                            name="mobile_no"
                            :telephone_no="contactDetails.mobile_no"
                            @telephone_no="contactDetails.mobile_no = $event"
                            :telephone_code="contactDetails.mobile_international_dialling_code"
                            @telephone_code="contactDetails.mobile_international_dialling_code = $event"
                            @change="updateContactDetails"
                            :options="collections.countries"
                        />
                    </div>
                </AccordionComponent>
            </ValidationObserver>
        </ValidationObserver>
    </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { ValidationObserver } from "vee-validate";
import InputComponent from "@components/inputs/InputComponent";
import DatepickerComponent from "@components/inputs/DatepickerComponent";
import AccordionComponent from "@components/misc/AccordionComponent";
import YesNoSliderComponent from "@components/inputs/YesNoSliderComponent";
import stepperValidationService from "@services/stepperValidationService";
import TelephoneComponent from "@components/inputs/TelephoneComponent";

export default {
    name: "PersonalDetailsFormComponent",
    components: {
        ValidationObserver,
        InputComponent,
        DatepickerComponent,
        AccordionComponent,
        YesNoSliderComponent,
        TelephoneComponent,
    },
    props: {
        step: { default: "" },
    },
    computed: {
        ...mapGetters(["user", "collections", "personalDetailsState"]),
    },
    data() {
        return {
            isMailingAddressDifferent: false,
            showOtherNationalityField: false,
            isFormValidated: false,
            personalDetails: {},
            addressDetails: {
                current: {},
                previous: {},
                mailing: {},
            },
            contactDetails: {
                telephone_international_dialling_code: { iso_code_2: "GB", international_dialling_code: "+44" }, // Default values
                telephone_no: null,
                mobile_international_dialling_code: { iso_code_2: "GB", international_dialling_code: "+44" }, // Default values
                mobile_no: null,
            },
        };
    },
    methods: {
        ...mapActions([
            "doGetPersonalDetails",
            "doGetContactDetails",
            "doUpdatePersonalDetails",
            "doGetAddressDetails",
            "doUpdateAddressDetails",
            "doUpdateContactDetails",
            "doClearAddressDetails",
            "doSetStepperStepStatus",
            "doGetCollectionData",
        ]),
        updatePersonalDetails() {
            console.log("this.personalDetails", this.personalDetails);
            this.doUpdatePersonalDetails(this.personalDetails);
        },
        updateAddressDetails() {
            this.doUpdateAddressDetails(this.addressDetails);
        },
        updateContactDetails() {
            this.doUpdateContactDetails(this.contactDetails);
        },
        doShowOtherNationalityField() {
            this.showOtherNationalityField = true;
        },
        checkIsMailingAddressDifferent() {
            let mailingAddressHasValues = Object.keys(this.addressDetails.mailing).filter((mailingDetail) => {
                return mailingDetail;
            }).length;

            this.isMailingAddressDifferent = !!mailingAddressHasValues;
        },
        setIsMailingAddressDifferent(isDifferent = false) {
            if (!isDifferent) this.doClearAddressDetails("mailing");
            this.isMailingAddressDifferent = isDifferent;
        },
        doGetValidationFormStatus(details, validation, isSoftCheck = false) {
            return stepperValidationService.getValidationFormStatus(details, validation);
        },
        doValidateForm() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.personalDetails,
                this.$refs.personalDetailsObserver,
                false,
                true
            );
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.contactDetails,
                this.$refs.personalDetailsObserver,
                false,
                true
            );
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.addressDetails,
                this.$refs.personalDetailsObserver,
                false,
                true
            );
        },
    },
    mounted() {
        this.$root.$on("submittingApplication", () => this.doValidateForm());
        this.$root.$on("stepChanged", (step) => {
            if (this.step < step && !this.isFormValidated) {
                this.doValidateForm();
                this.isFormValidated = true;
            } else if (this.step == step) {
                this.isFormValidated = false;
            }
        });
        this.doGetPersonalDetails().then((response) => {
            this.personalDetails = response.data;
        });
        this.doGetContactDetails().then((response) => {
            if (Object.keys(response.data).length) {
                // Only apply the change if there are values in the response
                this.contactDetails = response.data;
            }
        });
        this.doGetAddressDetails().then((response) => {
            if (Object.keys(response.data).length) {
                this.addressDetails = response.data;
            }
            this.checkIsMailingAddressDifferent();
        });

        this.checkIsMailingAddressDifferent();
        this.doGetCollectionData("titles");
        this.doGetCollectionData("countries");

        this.$gtm.trackView('Personal Details', 'currentPath');
    },
    watch: {
        "addressDetails.current.duration_at_address"() {
            if (this.addressDetails.current.duration_at_address >= 3) {
                this.doClearAddressDetails("previous");
            }
        },
        personalDetails() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.personalDetails,
                this.$refs.personalDetailsObserver
            );
        },
        addressDetails() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.addressDetails,
                this.$refs.personalDetailsObserver
            );
        },
        contactDetails() {
            stepperValidationService.checkStepperStepStatus(
                this.step,
                this.contactDetails,
                this.$refs.personalDetailsObserver
            );
        },
    },
};
</script>
